import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Lock } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { api } from "@/lib/api";

const SECTORS = ["all", "sovereign", "corporate_debt", "trade_finance", "infrastructure", "private_credit"];
const STATUSES = ["all", "pre_market", "live", "closed"];

export default function DealFlow() {
  const [deals, setDeals] = useState([]);
  const [sector, setSector] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [country, setCountry] = useState("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/deals").then((r) => setDeals(r.data));
  }, []);

  const countries = useMemo(
    () => ["all", ...Array.from(new Set(deals.map((d) => d.country)))],
    [deals]
  );

  const filtered = deals.filter(
    (d) =>
      (sector === "all" || d.sector === sector) &&
      (statusFilter === "all" || d.status === statusFilter) &&
      (country === "all" || d.country === country) &&
      (!search || d.title.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <DashboardLayout>
      <div className="border-b border-border px-6 md:px-10 py-6">
        <div className="overline gold-text">Active Pipeline</div>
        <h1 className="font-display font-medium text-3xl md:text-4xl tracking-tight mt-1">Deal Flow</h1>
      </div>

      <div className="px-6 md:px-10 py-6 space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 border border-border rounded-md px-3 h-10 flex-1 min-w-[240px] max-w-md">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search deals…"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="deals-search-input"
            />
          </div>

          <select value={country} onChange={(e) => setCountry(e.target.value)} className="h-10 bg-transparent border border-border rounded-md px-3 text-sm outline-none" data-testid="deals-country-filter">
            {countries.map((c) => (
              <option key={c} value={c} className="bg-[hsl(var(--background))]">{c === "all" ? "All Countries" : c}</option>
            ))}
          </select>

          <select value={sector} onChange={(e) => setSector(e.target.value)} className="h-10 bg-transparent border border-border rounded-md px-3 text-sm outline-none" data-testid="deals-sector-filter">
            {SECTORS.map((s) => (
              <option key={s} value={s} className="bg-[hsl(var(--background))]">{s === "all" ? "All Sectors" : s.replaceAll("_", " ")}</option>
            ))}
          </select>

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 bg-transparent border border-border rounded-md px-3 text-sm outline-none" data-testid="deals-status-filter">
            {STATUSES.map((s) => (
              <option key={s} value={s} className="bg-[hsl(var(--background))]">{s === "all" ? "All Status" : s.replaceAll("_", "-")}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="card-institutional overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 overline font-normal">Deal</th>
                <th className="px-5 py-3 overline font-normal">Country</th>
                <th className="px-5 py-3 overline font-normal">Type</th>
                <th className="px-5 py-3 overline font-normal text-right">Return</th>
                <th className="px-5 py-3 overline font-normal text-right">Size</th>
                <th className="px-5 py-3 overline font-normal">Status</th>
                <th className="px-5 py-3 overline font-normal text-right">Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => navigate(`/deals/${d.id}`)}
                  className="border-b border-border last:border-0 hover:bg-[hsl(var(--surface-elevated))] cursor-pointer transition-colors"
                  data-testid={`deal-row-${d.id}`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      {!d.accessible && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                      <span className="font-medium">{d.title}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-muted-foreground">{d.country}</td>
                  <td className="px-5 py-4 text-muted-foreground capitalize">{d.deal_type}</td>
                  <td className="px-5 py-4 font-mono text-right gold-text">{d.return_text}</td>
                  <td className="px-5 py-4 font-mono text-right">{d.size_usd}</td>
                  <td className="px-5 py-4"><StatusBadge status={d.status} /></td>
                  <td className="px-5 py-4 font-mono text-right text-xs text-muted-foreground">
                    {new Date(d.updated_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">No deals match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
